import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import whatsappIcon from "../../../src/assets/images/whatsappgray.svg";
import apple from "../../assets/images/Apple.svg";
import googleplay from "../../assets/images/Google.svg";
import callIcon from "../../assets/images/phonegray.svg";
import emailIcon from "../../assets/images/email.svg";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import useScreenResolution from "../../hooks/useScreenResolution";
import { emergencyServicesData } from "../dashboard/data";
import Breadcrumb from "../lifeInsurancePages/PolicyOverview/Breadcrumb";

// --- helpers (keep outside components) ---
const normalizePhone = (s = "") =>
  s
    .trim()
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

// If you need WhatsApp E.164 without +:
const toWhatsAppDigits = (s = "") => normalizePhone(s).replace(/^\+/, "");

// --- components ---
const AssistanceCard = ({ items, data }) => {
  const translate = useLanguageTranslate();

  return (
    <div className="w-[90%] max-w-[100%] ipad:w-[100%] max767:w-[100%]">
      {data && (
        <div className="flex items-center space-x-2 ml-2 mb-4 mt-5">
          <img
            src={data?.icon}
            alt={translate(data?.altText_en, data?.altText_ar)}
            className="w-8 h-8 mr-0 rtl:ml-2"
          />
          <h3 className="text-black text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] rtl:mr-2 rtl:pl-0">
            {translate(data?.title_en, data?.title_ar)}
          </h3>
        </div>
      )}

      <div className="space-y-4">
        {items.map((item, index) => {
          // `phone` accepts a string or a list. The first number keeps the
          // whole-card tap it always had; any extras render as their own
          // rows so a second assistance line is reachable too.
          const phones = item.phone
            ? [].concat(item.phone).filter(Boolean)
            : [];
          const telHref = phones[0] ? `tel:${normalizePhone(phones[0])}` : null;

          const handleCardClick = () => {
            if (telHref) window.location.href = telHref;
          };

          const handleCardKeyDown = (e) => {
            if (!telHref) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              window.location.href = telHref;
            }
          };

          return (
            <div
              key={index}
              role={telHref ? "button" : undefined}
              tabIndex={telHref ? 0 : undefined}
              onClick={telHref ? handleCardClick : undefined}
              onKeyDown={telHref ? handleCardKeyDown : undefined}
              className={[
                "flex items-center gap-4 self-stretch bg-[#F6F8F9] p-6 rounded-2xl min-h-[116px]",
                "border border-solid border-[#E5E9EB]",
                telHref
                  ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2"
                  : "",
                "transition-opacity duration-150 hover:opacity-90",
              ].join(" ")}
            >
              {item.icon && (
                <img
                  src={item.icon}
                  alt={translate(item.label_en, item.label_ar)}
                  className="w-[64px] h-[64px] mr-0"
                />
              )}

              <div className="flex-1">
                <p className="text-[#48535B] text-left text-lg not-italic font-medium leading-[120%] mb-3 rtl:text-right">
                  {translate(item.label_en, item.label_ar)}
                </p>

                {phones.map((phone, phoneIndex) => (
                  <div key={phone} className="flex items-center gap-2 mt-3">
                    <img src={callIcon} alt="" className="w-5 h-5 rtl:ml-2" />
                    <span className="text-[#48535B] text-sm not-italic font-semibold leading-6 tracking-[-0.084px]">
                      {translate("Call:", "اتصال")}{" "}
                      {phoneIndex === 0 ? (
                        <span dir="ltr">{phone}</span>
                      ) : (
                        // The card as a whole dials the first number, so an
                        // extra one needs its own link and has to keep the tap
                        // from reaching that handler.
                        <a
                          href={`tel:${normalizePhone(phone)}`}
                          dir="ltr"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          {phone}
                        </a>
                      )}
                    </span>
                  </div>
                ))}

                {item.email && (
                  <div className="flex items-center gap-2 mt-3">
                    <img src={emailIcon} alt="" className="w-5 h-5 rtl:ml-2" />
                    <span className="text-[#48535B] text-sm not-italic font-semibold leading-6 tracking-[-0.084px]">
                      {translate("Email:", "البريد الإلكتروني:")}{" "}
                      <a
                        href={`mailto:${item.email}`}
                        dir="ltr"
                        className="break-all"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {item.email}
                      </a>
                    </span>
                  </div>
                )}

                {item.link && (
                  <div className="flex items-center gap-2 mt-2">
                    <img
                      src={whatsappIcon}
                      alt="whatsapp"
                      className="w-5 h-5 rtl:ml-2"
                    />
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="underline"
                    >
                      {translate(item.linkTextEn, item.linkTextAr)}
                    </a>
                  </div>
                )}

                {(item.iosLink || item.gPayLink) && (
                  <div className="flex items-center gap-2 mt-3">
                    {item.iosLink && (
                      <a
                        href={item.iosLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <img src={apple} alt="App Store" className="h-10" />
                      </a>
                    )}
                    {item.gPayLink && (
                      <a
                        href={item.gPayLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <img
                          src={googleplay}
                          alt="Google Play"
                          className="h-10"
                        />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmergencyServices = ({ data, type }) => {
  const [breadCrumbData, setBreadCrumbData] = useState([]);
  const screenResolution = useScreenResolution();
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();

  const serviceData = data ? data : emergencyServicesData;

  useEffect(() => {
    setBreadCrumbData([
      {
        label: translate("Online Services", "الخدمات عبر الإنترنت"),
        link: `/`,
        clickable: true,
      },
      {
        label: translate("Emergency Services", "خدمات الطوارئ"),
        clickable: false,
        isActive: true,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.resolvedLanguage]);

  return (
    <div className="container mx-auto p-6 flex-col flex-wrap max767:p-5 emegencymodal">
      <div className="flex justify-between items-center">
        {!screenResolution.mobile && <Breadcrumb items={breadCrumbData} />}
        {/* {!screenResolution.mobile && (
          <span className="hidden last-login text-xs not-italic font-normal leading-4 text-gray-700">
            Last Login: 03 November 2024 at 05:20{" "}
          </span>
        )} */}
      </div>

      <div className="grid grid-cols-2 gap-6 w-[1000px] max-w-[100%] max767:grid-cols-1 mt-6 max767:mt-2">
        {serviceData.map((section, index) => (
          <AssistanceCard
            key={index}
            items={section?.items}
            data={!type && section?.data}
          />
        ))}
      </div>
    </div>
  );
};

export default EmergencyServices;
